import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ImpersonationBroker, type IPagedCollection, asApiError } from '../../../extViewmodels';
import type { IConversation } from '../../../extViewmodels/TextMessagingViewModel';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

const PAGE_SIZE = 50;
const REFETCH_INTERVAL_MS = 30_000;

export type UnreadTextCountByE164 = ReadonlyMap<string, number>;

export function toUnreadCountByE164(conversations: readonly IConversation[]): UnreadTextCountByE164 {
	const counts = new Map<string, number>();
	for (const conversation of conversations) {
		if (!conversation.unreadMessageCount) continue;
		for (const recipient of conversation.toNumbers ?? []) {
			const e164 = recipient.number?.e164;
			if (!e164) continue;
			counts.set(e164, (counts.get(e164) ?? 0) + conversation.unreadMessageCount);
		}
	}
	return counts;
}

/**
 * Unread conversations on the rep's texting number, keyed by the contact's
 * E.164. Polls because the Coffee bundle does not run the main app's STOMP
 * feed; window focus also refetches so a rep returning from their phone sees
 * the badge without waiting.
 */
export function useUnreadTextConversations(
	phoneNumberId: string | undefined,
	{ enabled = true }: { enabled?: boolean } = {}
): UseQueryResult<UnreadTextCountByE164, Error> {
	const userSession = useUserSession();

	return useQuery<UnreadTextCountByE164, Error>({
		queryKey: queryKeys.focusedView.unreadTextConversations(phoneNumberId),
		enabled: enabled && !!phoneNumberId,
		refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
		refetchOnWindowFocus: true,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IPagedCollection<IConversation>>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `conversation/phoneNumber/${phoneNumberId}`,
					queryParams: { unreadOnly: true, pageSize: PAGE_SIZE },
				}),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return toUnreadCountByE164(result.value?.values ?? []);
		},
		meta: { errorLogging: { category: 'FocusedViewTexting', action: 'UnreadTextConversationsQuery' } },
	});
}
